"use client";

import { useEffect, useState, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import LandingNav from "@/app/(marketing)/_components/landing-nav";
import LandingFooter from "@/components/LandingFooter";
import {
  Wrench,
  FileText,
  Users,
  BarChart3,
  MessageSquare,
  Zap,
  HardHat,
  ChevronRight,
  Sparkles,
  ClipboardList,
  Calculator,
} from "lucide-react";
import { useTranslations } from "@/lib/i18n-utils";
import { SignUpButton } from "@clerk/nextjs";

// Examples and trade questions are localized inside the component

function TypingText({ text, active }: { text: string; active: boolean }) {
  const [displayed, setDisplayed] = useState("");
  const idx = useRef(0);

  useEffect(() => {
    if (!active) {
      setDisplayed("");
      idx.current = 0;
      return;
    }
    const interval = setInterval(() => {
      if (idx.current < text.length) {
        setDisplayed(text.slice(0, idx.current + 1));
        idx.current++;
      } else {
        clearInterval(interval);
      }
    }, 18);
    return () => clearInterval(interval);
  }, [active, text]);

  return <span>{displayed}</span>;
}

export default function SvenAIPage() {
  const { locale } = useTranslations();
  // Localized chat examples
  const CHAT_EXAMPLES =
    locale === "da"
      ? [
          {
            user: "Hvad er den bedste silikonefuge til et vådrum?",
            ai: "Til vådrum vil jeg anbefale Soudal Fix All Crystal eller Dow Corning 785 — begge er mugresistente og tåler konstant fugt. Påfør på en tør overflade ved 10–15 °C og lad hærde i 24 timer før kontakt med vand.",
          },
          {
            user: "Opret et tilbud til en badeværelsesrenovering — 3 dages arbejde, materialer 640 DKK",
            ai: "Færdig. Tilbud #QT-0041 udarbejdet: 3 dage arbejde + 640 DKK i materialer + moms. PDF klar til at sende til kunden.",
          },
          {
            user: "Hvor lang tid skal fliseklæb tørre før fuge?",
            ai: "Ved almindeligt cementbaseret klæb: vent 24 timer før fuge under normale forhold. I koldt eller fugtigt vejr kan det tage op til 48 timer. Nogle hurtigklæbere er klar på 3–4 timer — tjek posen.",
          },
          {
            user: "Tilføj en ny kunde — Dave Harris, Telefon 12 34 56 78",
            ai: "Kunde tilføjet. Dave Harris er nu i dine kontakter med mobil 12 34 56 78. Skal jeg oprette et job til ham med det samme?",
          },
        ]
      : [
          {
            user: "What's the best silicone sealant for a wet room?",
            ai: "For wet rooms I'd go with Soudal Fix All Crystal or Dow Corning 785 — both are mould-resistant and handle constant moisture. Apply to a dry surface at 10–15 °C and allow 24 h cure before water contact.",
          },
          {
            user: "Create a quote for a bathroom refit — 3 days labour, materials 640 DKK",
            ai: "Done. Quote #QT-0041 drafted: 3 days labour + 640 DKK materials + VAT. PDF ready to send to the customer.",
          },
          {
            user: "How long does tile adhesive need before grouting?",
            ai: "Standard cement-based adhesive: wait 24 h before grouting in normal conditions. In cold or damp settings allow up to 48 h. Check the bag — some rapid-set products are ready in 3–4 h.",
          },
          {
            user: "Add a new customer — Dave Harris, Phone 12 34 56 78",
            ai: "Customer added. Dave Harris is now in your contacts with mobile 12 34 56 78. Want me to create a job for him straight away?",
          },
        ];

  // Localized trade question chips with canned demo answers
  const tradeQuestions =
    locale === "da"
      ? [
          {
            q: "Bedste klæb til gulvvarmefliser?",
            a: "Brug en fleksibel klæb i klasse C2TE S1 (fx Weber REP 05 eller Mira 3230) — den optager bevægelserne fra gulvvarmen. Sluk varmen 48 timer før fliselægning og skru langsomt op igen efter fugning.",
          },
          {
            q: "Hvor længe skal puds tørre før maling?",
            a: "Tommelfingerregel: 1 mm puds = 1 dags tørretid ved 20 °C og god udluftning. En typisk 10 mm puds skal altså have ca. 10–14 dage. Mal først når fugtmåleren viser under 1 %.",
          },
          {
            q: "Korrekt moment for 22mm kompressionsfittings?",
            a: "22 mm kompressionsfittings skal typisk have ca. 25–30 Nm — i praksis håndstramt plus 3/4 til 1 omgang med nøglen. Overspænd ikke; det deformerer olivenringen og giver utætheder.",
          },
          {
            q: "Hvilke værnemidler skal jeg bruge til mugfjernelse?",
            a: "Minimum: FFP3-maske, tætsluttende briller, nitrilhandsker og engangsdragt. Ved større angreb (>3 m²) bør området afskærmes med undertryk, og affald skal i lukkede poser.",
          },
          {
            q: "Er der særlige byggestandarder, vi skal følge?",
            a: "I Danmark er det primært Bygningsreglementet (BR18) plus de relevante DS/EN-standarder for dit fag — fx DS 1084 for vådrum og DS 432 for afløb. Sig hvilket job det gælder, så finder jeg de relevante krav.",
          },
          {
            q: "Er træsorten korrekt til formålet?",
            a: "Det afhænger af anvendelsen: udendørs bærende konstruktioner kræver trykimprægneret eller naturligt holdbart træ (fx lærk, eg) i anvendelsesklasse 3. Fortæl mig hvor træet skal bruges, så tjekker jeg det.",
          },
        ]
      : [
          {
            q: "Best adhesive for underfloor heating tiles?",
            a: "Use a flexible C2TE S1 class adhesive (e.g. Weber REP 05 or Mapei Keraflex) — it absorbs the movement from the heating cycle. Switch the heating off 48 h before tiling and ramp it back up slowly after grouting.",
          },
          {
            q: "How long to leave render before painting?",
            a: "Rule of thumb: 1 mm of render = 1 day of drying at 20 °C with good ventilation. A typical 10 mm coat needs 10–14 days. Only paint once a moisture meter reads below 1%.",
          },
          {
            q: "Correct torque for 22mm compression fittings?",
            a: "22 mm compression fittings typically want around 25–30 Nm — in practice, hand-tight plus 3/4 to 1 full turn with the spanner. Don't overtighten; it deforms the olive and causes leaks.",
          },
          {
            q: "What PPE do I need for mould removal?",
            a: "Minimum: FFP3 mask, sealed goggles, nitrile gloves, and a disposable coverall. For larger outbreaks (>3 m²), seal off the area with negative pressure and bag all waste in sealed sacks.",
          },
          {
            q: "Are there any specific building standards we need to follow?",
            a: "In Denmark it's primarily the Building Regulations (BR18) plus the relevant DS/EN standards for your trade — e.g. DS 1084 for wet rooms and DS 432 for drainage. Tell me which job it's for and I'll pull up the relevant requirements.",
          },
          {
            q: "Is the wood type suitable for the purpose?",
            a: "Depends on the application: outdoor load-bearing structures need pressure-treated or naturally durable timber (e.g. larch, oak) rated for use class 3. Tell me where the wood is going and I'll check it for you.",
          },
        ];
  const [chatIdx, setChatIdx] = useState(0);
  const [showAI, setShowAI] = useState(false);
  const [cycling, setCycling] = useState(true);
  const [pinned, setPinned] = useState<{ user: string; ai: string } | null>(null);

  // Cycle through chat examples
  useEffect(() => {
    if (!cycling || pinned) return;
    setShowAI(false);
    const t1 = window.setTimeout(() => setShowAI(true), 700);
    const t2 = window.setTimeout(() => {
      setChatIdx((i) => (i + 1) % CHAT_EXAMPLES.length);
      setCycling(false);
      window.setTimeout(() => setCycling(true), 400);
    }, 5500);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [chatIdx, cycling, pinned]);

  // Play a clicked suggested question in the demo chat
  useEffect(() => {
    if (!pinned) return;
    setShowAI(false);
    const t1 = window.setTimeout(() => setShowAI(true), 900);
    const t2 = window.setTimeout(() => {
      setPinned(null);
      setShowAI(false);
      setCycling(true);
    }, 12000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [pinned]);

  const askQuestion = (q: string, a: string) => {
    setCycling(false);
    setShowAI(false);
    setPinned({ user: q, ai: a });
  };

  const current = pinned ?? CHAT_EXAMPLES[chatIdx];

  const capabilities = [
    {
      icon: Calculator,
      title: locale === "da" ? "Lav tilbud" : "Create offers",
      description:
        locale === "da"
          ? "Beskriv jobbet og Svendskriver tilbuddet — inklusive materialer, arbejdstid og moms."
          : "Describe the job and Svendwrites the quote — materials, labour, and VAT included.",
      accent: "#F97316",
    },
    {
      icon: ClipboardList,
      title: locale === "da" ? "Styr jobs" : "Manage jobs",
      description:
        locale === "da"
          ? "Opret, opdater og følg jobs med blot en besked. Ingen skemaer, ingen spild af tid."
          : "Create, update, and track jobs with a single message. No forms, no wasted clicks.",
      accent: "#F97316",
    },
    {
      icon: Users,
      title: locale === "da" ? "Kunderegistrering" : "Customer records",
      description:
        locale === "da"
          ? "Tilføj kunder, gem kontaktoplysninger og find historik uden at lede."
          : "Add customers, store contact details, and pull up history without digging.",
      accent: "#F97316",
    },
    {
      icon: BarChart3,
      title: locale === "da" ? "Rapporter" : "Reports",
      description:
        locale === "da"
          ? "Bed om en rapport og få den straks — omætning, åbne jobs, udestående fakturaer."
          : "Ask for a report and get it instantly — revenue, open jobs, outstanding invoices.",
      accent: "#F97316",
    },
    {
      icon: Wrench,
      title: locale === "da" ? "Faguddannede råd" : "Trades knowledge",
      description:
        locale === "da"
          ? "Hvad tørrer hurtigst? Hvilken primer skal jeg bruge? Svend ved det."
          : "What dries fastest? Which primer do I need? Svend knows the trade inside out.",
      accent: "#F97316",
    },
    {
      icon: FileText,
      title: locale === "da" ? "Fakturaer" : "Invoices",
      description:
        locale === "da"
          ? "Konverter et tilbud til en faktura og send den til kunden på sekunder."
          : "Convert a quote to an invoice and send it to the customer in seconds.",
      accent: "#F97316",
    },
  ];
  

  return (
    <main className="min-h-screen bg-[#0D0D0D] text-slate-100 font-sans">
      <style jsx global>{`
        html, body { background: #0D0D0D !important; }
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@400;500&display=swap');
        .sven-display { font-family: 'Syne', sans-serif; }
        .sven-body { font-family: 'DM Sans', sans-serif; }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideInLeft {
          from { opacity: 0; transform: translateX(-10px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(10px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes pulseOrange {
          0%, 100% { box-shadow: 0 0 0 0 rgba(249,115,22,0.35); }
          50%       { box-shadow: 0 0 0 10px rgba(249,115,22,0); }
        }
        .cap-card:hover {
          border-color: rgba(249,115,22,0.45) !important;
          background: rgba(249,115,22,0.06) !important;
          transform: translateY(-2px);
        }
        .cap-card { transition: all 0.22s ease; }

        .trade-pill:hover {
          background: rgba(249,115,22,0.15) !important;
          border-color: rgba(249,115,22,0.5) !important;
          color: #fb923c !important;
        }
        .trade-pill { transition: all 0.18s ease; cursor: pointer; }

      `}</style>

      {/* ── NAV ── */}
      <div className="relative z-50 mx-auto max-w-7xl px-6 py-6 md:px-10">
        <LandingNav lang={locale as "da" | "en"} dark />
      </div>

      {/* ── HERO ── */}
      <section className="relative overflow-hidden">
        {/* orange radial bloom */}
        <div className="pointer-events-none absolute left-1/2 top-0 h-[520px] w-[820px] -translate-x-1/2 -translate-y-1/4 rounded-full bg-[radial-gradient(ellipse,rgba(249,115,22,0.22),transparent_65%)]" />

        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-6 pb-24 pt-10 md:px-10 md:pt-14 lg:grid-cols-[1fr_400px]">
          <div>
          {/* badge */}
          <div
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-orange-500/30 bg-orange-500/10 px-4 py-1.5 text-xs font-medium text-orange-400"
            style={{ animation: "fadeUp 0.5s ease both" }}
          >
            <Sparkles className="h-3.5 w-3.5" />
            {locale === "da" ? "AI-assistent til håndværkere" : "AI assistant built for the trades"}
          </div>

          {/* headline */}
          <h1
            className="sven-display mb-6 max-w-4xl text-5xl font-extrabold leading-[1.05] tracking-tight text-white md:text-7xl"
            style={{ animation: "fadeUp 0.55s 0.08s ease both" }}
          >
            {locale === "da" ? (
              <>
                Din håndværker-AI.
                <br />
                <span className="text-orange-400">Klar på jobbet.</span>
              </>
            ) : (
              <>
                Your trade AI.
                <br />
                <span className="text-orange-400">Ready on the tools.</span>
              </>
            )}
          </h1>

          <p
            className="sven-body mb-10 max-w-xl text-lg leading-relaxed text-slate-400"
            style={{ animation: "fadeUp 0.55s 0.16s ease both" }}
          >
            {locale === "da"
              ? "Svend besvarer faglige spørgsmål, opretter tilbud, kunder og jobs — og skriver rapporter. Alt med en besked."
              : "Svend answers trade questions, creates quotes, customers and jobs, and writes reports — all from a single message."}
          </p>

          <div
            className="flex flex-wrap gap-3"
            style={{ animation: "fadeUp 0.55s 0.22s ease both" }}
          >
            <SignUpButton mode="modal" fallbackRedirectUrl="/org/create">
              <button className="sven-body inline-flex items-center gap-2 rounded-xl bg-orange-500 px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-orange-500/30 transition hover:bg-orange-400 active:scale-95">
                <HardHat className="h-4 w-4" />
                {locale === "da" ? "Prøv Svend" : "Try Svend"}
                <ChevronRight className="h-4 w-4 opacity-70" />
              </button>
            </SignUpButton>
            <Link
              href="/pricing"
              className="sven-body inline-flex items-center gap-2 rounded-xl border border-orange-500/15 bg-white/5 px-6 py-3.5 text-sm font-semibold text-white transition hover:bg-white/10"
            >
              {locale === "da" ? "Se priser" : "View pricing"}
            </Link>
          </div>
          </div>

          {/* hero phone image */}
          <div
            className="relative mx-auto hidden w-full max-w-[400px] lg:block"
            style={{ animation: "fadeUp 0.6s 0.25s ease both" }}
          >
            <div className="pointer-events-none absolute inset-0 scale-110 rounded-full bg-[radial-gradient(ellipse,rgba(249,115,22,0.18),transparent_70%)]" />
            <Image
              src="/iPhone App Promo(17).png"
              alt={locale === "da" ? "Svend AI-assistenten på mobilen" : "The Svend AI assistant on mobile"}
              width={1080}
              height={1610}
              priority
              className="relative w-full drop-shadow-2xl"
            />
          </div>
        </div>
      </section>

      {/* ── LIVE CHAT DEMO ── */}
      <section className="mx-auto max-w-7xl px-6 pb-20 md:px-10">
        <div className="grid gap-6 lg:grid-cols-[1fr_420px]">

          {/* left — chat window */}
          <div className="rounded-2xl border border-orange-500/15 bg-[#141414] overflow-hidden">
            {/* chrome bar */}
            <div className="flex items-center gap-3 border-b border-orange-500/15 bg-[#111] px-5 py-3.5">
              <div className="h-3 w-3 rounded-full bg-red-500/70" />
              <div className="h-3 w-3 rounded-full bg-yellow-500/70" />
              <div className="h-3 w-3 rounded-full bg-green-500/70" />
              <div className="ml-3 flex items-center gap-2">
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-orange-500 text-[10px] font-bold text-white sven-display">S</div>
                <span className="sven-body text-sm text-slate-400">SvendAI</span>
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" style={{ animation: "pulseOrange 2s ease-in-out infinite" }} />
              </div>
            </div>

            {/* messages */}
            <div className="flex flex-col gap-4 p-6 min-h-[240px]">
              {/* user bubble */}
              <div
                key={`user-${current.user}`}
                className="self-end max-w-[78%] rounded-2xl rounded-tr-sm bg-orange-500/15 border border-orange-500/20 px-4 py-3 text-sm text-slate-200 sven-body"
                style={{ animation: "slideInRight 0.35s ease both" }}
              >
                {current.user}
              </div>

              {/* AI bubble */}
              {showAI && (
                <div
                  key={`ai-${current.user}`}
                  className="self-start max-w-[82%] rounded-2xl rounded-tl-sm bg-[#1C1C1C] border border-orange-500/15 px-4 py-3 text-sm text-slate-300 sven-body"
                  style={{ animation: "slideInLeft 0.35s ease both" }}
                >
                  <div className="mb-2 flex items-center gap-1.5">
                    <div className="flex h-5 w-5 items-center justify-center rounded-full bg-orange-500 text-[9px] font-bold text-white sven-display">S</div>
                    <span className="text-xs text-orange-400 font-medium">Sven</span>
                  </div>
                  <TypingText text={current.ai} active={showAI} />
                </div>
              )}

              {!showAI && (
                <div className="self-start flex items-center gap-1 px-4 py-3">
                  <span className="h-2 w-2 rounded-full bg-slate-600 animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="h-2 w-2 rounded-full bg-slate-600 animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="h-2 w-2 rounded-full bg-slate-600 animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              )}
            </div>

            {/* input bar */}
            <div className="border-t border-orange-500/15 bg-[#111] px-5 py-3 flex items-center gap-3">
              <div className="flex-1 rounded-xl bg-white/5 border border-orange-500/15 px-4 py-2.5 text-sm text-slate-500 sven-body">
                {locale === "da" ? "Skriv til Sven…" : "Ask Svendanything…"}
              </div>
              <button className="flex h-9 w-9 items-center justify-center rounded-xl bg-orange-500 text-white transition hover:bg-orange-400">
                <Zap className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* right — trade question chips */}
          <div className="flex flex-col justify-center gap-4">
            <p className="sven-body text-xs font-medium uppercase tracking-widest text-slate-500">
              {locale === "da" ? "Prøv disse spørgsmål" : "Try asking Sven"}
            </p>
            <div className="flex flex-col gap-2">
              {tradeQuestions.map(({ q, a }) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => askQuestion(q, a)}
                  className={`trade-pill sven-body flex items-center gap-3 rounded-xl border px-4 py-3 text-left text-sm ${
                    pinned?.user === q
                      ? "border-orange-500/50 bg-orange-500/15 text-orange-300"
                      : "border-orange-500/15 bg-white/4 text-slate-400"
                  }`}
                >
                  <MessageSquare className="h-4 w-4 shrink-0 text-orange-500/70" />
                  {q}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CAPABILITIES GRID ── */}
      <section className="mx-auto max-w-7xl px-6 pb-24 md:px-10">
        <div className="mb-10">
          <h2 className="sven-display text-3xl font-extrabold text-white md:text-4xl">
            {locale === "da" ? "Alt Svend kan gøre for dig" : "Everything Svend can do"}
          </h2>
          <p className="sven-body mt-3 max-w-lg text-slate-400">
            {locale === "da"
              ? "Fra hurtige faglige svar til fulde dokumenter — bare snak naturligt."
              : "From quick trade answers to full documents — just talk naturally."}
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {capabilities.map((cap) => {
            const Icon = cap.icon;
            return (
              <article
                key={cap.title}
                className="cap-card rounded-2xl border border-orange-500/15 bg-[#141414] p-6"
              >
                <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-orange-500/10 border border-orange-500/20">
                  <Icon className="h-5 w-5 text-orange-400" />
                </div>
                <h3 className="sven-display text-base font-bold text-white mb-2">{cap.title}</h3>
                <p className="sven-body text-sm leading-relaxed text-slate-400">{cap.description}</p>
              </article>
            );
          })}
        </div>
      </section>

      {/* ── STRIP STAT BAR ── */}
      <section className="border-y border-orange-500/15 bg-[#111]">
        <div className="mx-auto max-w-7xl px-6 py-10 md:px-10">
          <div className="grid grid-cols-2 gap-8 text-center md:grid-cols-4">
            {[
              { value: "< 10s", label: locale === "da" ? "Tilbud genereret" : "Quote generated" },
              { value: "24 / 7", label: locale === "da" ? "Altid tilgængelig" : "Always available" },
              { value: "100 +", label: locale === "da" ? "Faglige emner" : "Trade topics covered" },
              { value: "0", label: locale === "da" ? "Nødvendige skemaer" : "Forms to fill in" },
            ].map((s) => (
              <div key={s.label}>
                <p className="sven-display text-3xl font-extrabold text-orange-400 md:text-4xl">{s.value}</p>
                <p className="sven-body mt-1 text-sm text-slate-500">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="mx-auto max-w-7xl px-6 py-24 text-center md:px-10">
        <div className="relative mx-auto max-w-2xl">
          <div className="pointer-events-none absolute inset-0 rounded-3xl bg-[radial-gradient(ellipse_at_center,rgba(249,115,22,0.15),transparent_65%)]" />
          <div className="relative rounded-3xl border border-orange-500/20 bg-[#141414] px-8 py-14">
            <HardHat className="mx-auto mb-4 h-10 w-10 text-orange-400" />
            <h2 className="sven-display mb-4 text-3xl font-extrabold text-white md:text-4xl">
              {locale === "da" ? "Klar til at spare tid på jobbet?" : "Ready to save time on the tools?"}
            </h2>
            <p className="sven-body mx-auto mb-8 max-w-md text-slate-400">
              {locale === "da"
                ? "Prøv Svend med dit team i dag."
                : "Try Svend with your team today."}
            </p>
            <SignUpButton mode="modal" fallbackRedirectUrl="/org/create">
              <button className="sven-body inline-flex items-center gap-2 rounded-xl bg-orange-500 px-8 py-4 text-sm font-semibold text-white shadow-lg shadow-orange-500/30 transition hover:bg-orange-400 active:scale-95">
                <Sparkles className="h-4 w-4" />
                {locale === "da" ? "Kom i gang i dag" : "Get started today"}
              </button>
            </SignUpButton>
          </div>
        </div>
      </section>

      {/* Shared landing-page footer */}
      <LandingFooter lang={locale === "da" ? "da" : "en"} daHref="/ai" enHref="/en/ai" />
    </main>
  );
}